import { Link, useLocation } from "wouter";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import { Menu, Linkedin, Instagram, Facebook, Youtube } from "lucide-react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import * as CookieConsent from "vanilla-cookieconsent";
import LanguageSelector from "./LanguageSelector";
import { lazy, Suspense } from "react";

const Chatbot = lazy(() => import("./Chatbot"));

export default function Layout({ children }: { children: React.ReactNode }) {
  const [location] = useLocation();
  const [isOpen, setIsOpen] = useState(false);
  const { t } = useTranslation();

  const navItems = [
    { label: t("nav.home"), href: "/" },
    { label: t("nav.product"), href: "/produto" },
    { label: t("nav.about"), href: "/sobre" },
    { label: t("nav.blog"), href: "/blog" },
    { label: t("nav.contact"), href: "/contato" },
  ];

  return (
    <div className="min-h-screen flex flex-col bg-background text-foreground font-sans">
      {/* Navbar */}
      <header className="sticky top-0 z-50 w-full border-b border-border/40 bg-background/80 backdrop-blur supports-[backdrop-filter]:bg-background/60">
        <div className="container flex h-16 items-center justify-between">
          <Link href="/" className="flex items-center gap-2">
            <img
              src="/logo/white-blue.png"
              alt="SoverX.ai Logo"
              className="h-14 w-auto"
            />
          </Link>

          {/* Desktop Nav */}
          <div className="hidden md:flex items-center gap-6">
            <nav className="flex items-center gap-6">
              {navItems.map(item => (
                <Link
                  key={item.href}
                  href={item.href}
                  className={`text-sm font-medium transition-colors hover:text-primary ${
                    location === item.href
                      ? "text-primary"
                      : "text-muted-foreground"
                  }`}
                >
                  {item.label}
                </Link>
              ))}
            </nav>
            <div className="flex items-center gap-4 border-l border-border/40 pl-4">
              <LanguageSelector />
              <Link href="/solicitar-demo">
                <Button size="sm" className="font-semibold px-6">
                  {t("common.schedule_demo")}
                </Button>
              </Link>
            </div>
          </div>

          {/* Mobile Nav */}
          <div className="flex items-center gap-2 md:hidden">
            <LanguageSelector />
            <Sheet open={isOpen} onOpenChange={setIsOpen}>
              <SheetTrigger asChild>
                <Button
                  variant="ghost"
                  size="icon"
                  aria-label={t("common.menu")}
                >
                  <Menu className="h-6 w-6" />
                </Button>
              </SheetTrigger>
              <SheetContent side="right" className="w-[300px] sm:w-[400px]">
                <nav className="px-4 flex flex-col gap-4 mt-8">
                  {navItems.map(item => (
                    <Link
                      key={item.href}
                      href={item.href}
                      className={`text-lg font-medium transition-colors hover:text-primary ${
                        location === item.href
                          ? "text-primary"
                          : "text-muted-foreground"
                      }`}
                      onClick={() => setIsOpen(false)}
                    >
                      {item.label}
                    </Link>
                  ))}
                  <Link href="/solicitar-demo" onClick={() => setIsOpen(false)}>
                    <Button className="mt-4 w-full font-semibold">
                      {t("common.schedule_demo")}
                    </Button>
                  </Link>
                </nav>
              </SheetContent>
            </Sheet>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="flex-1">{children}</main>
      <Suspense fallback={null}>
        <Chatbot />
      </Suspense>

      {/* Footer */}
      <footer className="border-t border-border/40 bg-card py-12">
        <div className="container grid grid-cols-1 md:grid-cols-4 gap-8">
          <div className="col-span-1 md:col-span-2">
            <Link href="/" className="flex items-center gap-2 mb-4">
              <img
                src="/logo/white-blue.png"
                alt="SoverX.ai Logo"
                className="h-12 w-auto"
              />
            </Link>
            <p className="text-muted-foreground text-sm max-w-xs leading-relaxed mb-6">
              SoverX.ai — {t("hero.badge")}. {t("contact.description")}
            </p>
            <div className="flex gap-4">
              <a
                href="https://www.linkedin.com/company/soverx/"
                target="_blank"
                rel="noopener noreferrer"
                className="text-muted-foreground hover:text-primary transition-colors"
                aria-label="SoverX no LinkedIn"
              >
                <Linkedin className="h-5 w-5" />
              </a>
              <a
                href="https://www.instagram.com/soverx.ai/"
                target="_blank"
                rel="noopener noreferrer"
                className="text-muted-foreground hover:text-primary transition-colors"
                aria-label="SoverX no Instagram"
              >
                <Instagram className="h-5 w-5" />
              </a>
              <a
                href="https://www.facebook.com/soverXai"
                target="_blank"
                rel="noopener noreferrer"
                className="text-muted-foreground hover:text-primary transition-colors"
                aria-label="SoverX no Facebook"
              >
                <Facebook className="h-5 w-5" />
              </a>
              <a
                href="https://www.youtube.com/@soverX-ai"
                target="_blank"
                rel="noopener noreferrer"
                className="text-muted-foreground hover:text-primary transition-colors"
                aria-label="SoverX no YouTube"
              >
                <Youtube className="h-5 w-5" />
              </a>
            </div>
          </div>

          <div>
            <h3 className="font-semibold mb-4 text-sm uppercase tracking-wider text-muted-foreground">
              {t("nav.product")}
            </h3>
            <ul className="space-y-2 text-sm">
              <li>
                <Link
                  href="/produto"
                  className="hover:text-primary transition-colors"
                >
                  {t("nav.product")}
                </Link>
              </li>
              <li>
                <Link
                  href="/produto"
                  className="hover:text-primary transition-colors"
                >
                  {t("trust.badge")}
                </Link>
              </li>
              <li>
                <Link
                  href="/solicitar-demo"
                  className="hover:text-primary transition-colors"
                >
                  {t("common.schedule_demo")}
                </Link>
              </li>
            </ul>
          </div>

          <div>
            <h3 className="font-semibold mb-4 text-sm uppercase tracking-wider text-muted-foreground">
              {t("common.menu")}
            </h3>
            <ul className="space-y-2 text-sm">
              <li>
                <Link
                  href="/sobre"
                  className="hover:text-primary transition-colors"
                >
                  {t("nav.about")}
                </Link>
              </li>
              <li>
                <Link
                  href="/blog"
                  className="hover:text-primary transition-colors"
                >
                  {t("nav.blog")}
                </Link>
              </li>
              <li>
                <Link
                  href="/contato"
                  className="hover:text-primary transition-colors"
                >
                  {t("nav.contact")}
                </Link>
              </li>
              <li>
                <a
                  href="https://soverx.kretos.cc"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="hover:text-primary transition-colors"
                >
                  Trabalhe Conosco
                </a>
              </li>
              <li>
                <a
                  href="https://soverx.guardiaoetico.com.br"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="hover:text-primary transition-colors"
                >
                  Canal de Denúncias
                </a>
              </li>
            </ul>
          </div>
        </div>

        <div className="container mt-12 pt-8 border-t border-border/20 flex flex-col md:flex-row justify-between items-center gap-4 text-xs text-muted-foreground">
          <p>{t("common.copyright", { year: new Date().getFullYear() })}</p>
          <div className="flex gap-6">
            <Link
              href="/privacidade"
              className="hover:text-foreground transition-colors"
            >
              {t("common.privacy_policy")}
            </Link>
            <Link
              href="/termos"
              className="hover:text-foreground transition-colors"
            >
              {t("common.terms_of_use")}
            </Link>
            <Link
              href="#"
              className="hover:text-foreground transition-colors"
              aria-label="Gerenciar preferências de cookies"
              onClick={e => {
                e.preventDefault();
                CookieConsent.showPreferences();
              }}
            >
              Cookies
            </Link>
          </div>
        </div>
      </footer>
    </div>
  );
}
